import { type IHeroSection } from '../../commons/HeroSection'
import { type ITextSectionHyperLinkedImage } from '../../sections/ITextSectionHyperLinkedImage'
import { type IPage } from '../IPage'
import { Hit } from '../../rest/SearchResponse'
import { SearchResult } from '../../../../components/forms/fields/DropdownInput'

// Billing guide related types and interfaces

export interface IBillingGuideLookupPage extends IPage {
  heroSection: IHeroSection
  disclaimer: string
  ourAssociations: ITextSectionHyperLinkedImage[]
}

export enum ResultsStatus {
  IDLE = 'idle',
  SEARCHING = 'searching',
  SUCCESS = 'success',
  NO_RESULTS = 'no_results',
  ERROR = 'error',
}

// Billing guide page / hook props

export type ProcedureProps = {
  items: string[]
  selectedItem: string
  onSelect: (procedureName: string) => void
}

export type PostcodeProps = {
  suggestions: Hit<PostcodeData>[]
  value: string
  error: string | null
  loading: boolean
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onSelect: (item: PostcodeData) => void
  onCloseDropdown: () => void
}

export type ClinicProps = {
  items: string[]
  selectedItem: string | null
  error: string | null
  loading: boolean
  onSelect: (value: string) => void
}

export type BillingResult = {
  location: string
  procedure: string
  pensionerGap: string
  privateGap: string
  sourceTimestamp?: string
}

export type ResultsProps = {
  results: BillingResult[]
  status: ResultsStatus
  sourceTimestamp?: string
  error: string | null
  onSearch: () => void
}

// Saga return types

export interface PostcodeData extends SearchResult {
  postcode: string
  suburb: string
  lat: number
  lon: number
}

export type PricingData = {
  id: string
  clinicName: string
  modalityName: string
  subModalityName: string
  privateGap: string
  pensionerGap: string
  metadata?: {
    sourceTimestamp?: string
    processedAt: string
    sourceKey?: string
  }
  score?: number
}

// Data-only interfaces (without callbacks) for selectors
export interface PostcodeStateData {
  suggestions: Hit<PostcodeData>[]
  value: string
  error: string | null
  loading: boolean
}

export interface ResultsStateData {
  results: BillingResult[]
  status: ResultsStatus
  sourceTimestamp?: string
  error: string | null
}

// Clinic state data interface
export interface ClinicStateData {
  items: string[]
  selectedItem: string | null
  error: string | null
  loading: boolean
}

// Main state interface for the Billing Guide lookup page
export type BillingGuideState = {
  postcodes: Hit<PostcodeData>[]
  clinics: string[]
  pricing: PricingData[] | null // null = never searched, [] = searched but empty, [data] = results
  loading: {
    postcodes: boolean
    clinics: boolean
    pricing: boolean
  }
  error: {
    postcodes: string | null
    clinics: string | null
    pricing: string | null
  }
  postcodeInput: string
  selectedPostcode: PostcodeData | null
  selectedClinic: string | null
  selectedProcedure: string
}
